/**
 * Global Logger with Category Toggles
 * 
 * Usage:
 *   import { logger } from './utils/logger'
 *   logger.log('SOCKET', 'Message here')
 *   logger.warn('ASSET', 'Warning here')
 *   logger.error('API', 'Error here')
 * 
 * Configuration:
 *   1. Environment variables (in .env):
 *      VITE_LOG_ENABLED=true|false
 *      VITE_LOG_CATEGORIES=SOCKET,ASSET,API,PAYMENT,SHOP
 * 
 *   2. Runtime (in browser console, dev only):
 *      logger.enableCategory('SOCKET')
 *      logger.disableCategory('ASSET')
 *      logger.enableAll()
 *      logger.disableAll()
 * 
 * Categories:
 *   - SOCKET: Socket connection and message logs
 *   - ASSET: Asset loading and resolution logs
 *   - API: API calls and responses
 *   - PAYMENT: Payment-related events
 *   - SHOP: Shop data and checkout logs
 *   - COMPONENT: Component lifecycle logs
 *   - ALL: Enable all categories
 */

export type LogCategory =
    | 'SOCKET'
    | 'ASSET'
    | 'API'
    | 'AUTH'
    | 'COMPONENT'
    | 'PAYMENT'
    | 'SHOP'
    | 'REWARDS'
    | 'ALL'

interface LoggerConfig {
    enabled: boolean
    categories: Set<LogCategory>
}

class Logger {
    private config: LoggerConfig = {
        enabled: true,
        categories: new Set<LogCategory>(['ALL'])
    }

    /**
     * Initialize logger with configuration
     * Can be called with environment variables or explicit config
     */
    initialize(config?: {
        enabled?: boolean
        categories?: LogCategory[]
        enableAll?: boolean
    }): void {
        if (config?.enabled !== undefined) {
            this.config.enabled = config.enabled
        }

        if (config?.categories) {
            this.config.categories = new Set(config.categories)
        } else if (config?.enableAll) {
            this.config.categories = new Set(['ALL'])
        }

        // Support environment variable: VITE_LOG_CATEGORIES=SOCKET,ASSET,API
        if (typeof window !== 'undefined') {
            const envCategories = import.meta.env.VITE_LOG_CATEGORIES
            if (envCategories) {
                const categories = envCategories.split(',').map((c: string) => c.trim().toUpperCase() as LogCategory)
                this.config.categories = new Set(categories)
            }

            const envEnabled = import.meta.env.VITE_LOG_ENABLED
            if (envEnabled !== undefined) {
                this.config.enabled = envEnabled === 'true' || envEnabled === '1'
            }
        }
    }

    /**
     * Check if a category is enabled for logging
     */
    private isEnabled(category: LogCategory): boolean {
        if (!this.config.enabled) {
            return false
        }

        return (
            this.config.categories.has('ALL') ||
            this.config.categories.has(category)
        )
    }

    /**
     * Log a message with a category
     */
    log(category: LogCategory, ...args: unknown[]): void {
        if (!this.isEnabled(category)) {
            return
        }

        console.log(`[${category}]`, ...args)
    }

    /**
     * Log a warning with a category
     */
    warn(category: LogCategory, ...args: unknown[]): void {
        if (!this.isEnabled(category)) {
            return
        }

        console.warn(`[${category}]`, ...args)
    }

    /**
     * Log an error with a category (always enabled)
     */
    error(category: LogCategory, ...args: unknown[]): void {
        // Errors are always logged, but with category prefix
        console.error(`[${category}]`, ...args)
    }

    /**
     * Log info with a category
     */
    info(category: LogCategory, ...args: unknown[]): void {
        if (!this.isEnabled(category)) {
            return
        }

        console.info(`[${category}]`, ...args)
    }

    /**
     * Enable/disable specific categories at runtime
     */
    enableCategory(category: LogCategory): void {
        this.config.categories.add(category)
    }

    /**
     * Disable a specific category at runtime
     */
    disableCategory(category: LogCategory): void {
        this.config.categories.delete(category)
    }

    /**
     * Enable all logging
     */
    enableAll(): void {
        this.config.enabled = true
        this.config.categories = new Set(['ALL'])
    }

    /**
     * Disable all logging
     */
    disableAll(): void {
        this.config.enabled = false
    }

    /**
     * Get current configuration
     */
    getConfig(): Readonly<LoggerConfig> {
        return {
            enabled: this.config.enabled,
            categories: new Set(this.config.categories)
        }
    }
}

// Export singleton instance
export const logger = new Logger()

// Initialize with defaults on import
logger.initialize()

// Expose to window for runtime control (dev only)
if (typeof window !== 'undefined' && import.meta.env.DEV) {
    (window as unknown as { logger: Logger }).logger = logger
}

